// VideoChat.jsx

import React, { useEffect, useRef, useState, useCallback, memo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Peer from 'simple-peer';
import io from 'socket.io-client';
import { useAuth } from '../contexts/authcontext';
import { toast } from 'react-toastify';
import {
  Mic,
  MicOff,
  Videocam,
  VideocamOff,
  CallEnd,
  Chat as ChatIcon,
  Person,
  Send,
  VisibilityOff,
  Visibility
} from '@mui/icons-material';
import {
  Box,
  IconButton,
  Typography,
  Paper,
  TextField,
  Avatar,
  Badge
} from '@mui/material';

// ─────────────────────────────────────────────
// Anonymous mode helpers
// ─────────────────────────────────────────────

// Draws the local <video> element onto a canvas every frame with a blur
// filter applied, then captures that canvas as a MediaStream.
function createBlurredVideoTrack(sourceVideoEl, { width = 640, height = 480, blurPx = 14, fps = 15 } = {}) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');

  let rafId = null;
  let stopped = false;

  const draw = () => {
    if (stopped) return;
    try {
      ctx.filter = `blur(${blurPx}px)`;
      ctx.drawImage(sourceVideoEl, 0, 0, canvas.width, canvas.height);
    } catch (e) {
      // ignore transient draw errors (e.g. video not ready yet)
    }
    rafId = requestAnimationFrame(draw);
  };
  draw();

  const stream = canvas.captureStream(fps);
  const track = stream.getVideoTracks()[0];

  const stop = () => {
    stopped = true;
    if (rafId) cancelAnimationFrame(rafId);
    stream.getTracks().forEach(t => t.stop());
  };

  return { track, stream, stop };
}

// Builds a native Web Audio graph that disguises the voice a bit
function createVoiceDisguiseChain(audioContext) {
  const highpass = audioContext.createBiquadFilter();
  highpass.type = 'highpass';
  highpass.frequency.value = 200;

  const lowpass = audioContext.createBiquadFilter();
  lowpass.type = 'lowpass';
  lowpass.frequency.value = 3800;

  const pitchDelay = audioContext.createDelay(0.05);
  pitchDelay.delayTime.value = 0.01;

  const lfo = audioContext.createOscillator();
  lfo.frequency.value = 6; // Hz - subtle wobble rate
  const lfoGain = audioContext.createGain();
  lfoGain.gain.value = 0.0035; // wobble depth
  lfo.connect(lfoGain);
  lfoGain.connect(pitchDelay.delayTime);
  lfo.start();

  const destination = audioContext.createMediaStreamDestination();

  highpass.connect(lowpass);
  lowpass.connect(pitchDelay);
  pitchDelay.connect(destination);

  const stop = () => {
    try { lfo.stop(); } catch {}
    try { highpass.disconnect(); lowpass.disconnect(); pitchDelay.disconnect(); } catch {}
  };

  return { input: highpass, destination, stop };
}

// Shared ICE config. STUN-only fails behind symmetric NAT, which is common on
// Indian mobile carriers — uncomment the TURN entry once you have a server.
const ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  // {
  //   urls: 'turn:YOUR_TURN_SERVER_HOST:3478',
  //   username: process.env.REACT_APP_TURN_USERNAME,
  //   credential: process.env.REACT_APP_TURN_CREDENTIAL
  // }
];

// ─────────────────────────────────────────────
// RemoteVideo component
// ─────────────────────────────────────────────
const RemoteVideo = memo(({ peer, name }) => {
  const ref = useRef();
  const [hasStream, setHasStream] = useState(false);

  useEffect(() => {
    if (!peer) return;

    const handleStream = (stream) => {
      console.log(`[RemoteVideo] Stream received from: ${name}`);
      if (ref.current) {
        ref.current.srcObject = stream;
        setHasStream(true);
      }
    };

    const handleClose = () => setHasStream(false);
    const handleError = (err) => {
      console.error(`[RemoteVideo] Peer error for ${name}:`, err.message);
      setHasStream(false);
    };

    peer.on('stream', handleStream);
    peer.on('close', handleClose);
    peer.on('error', handleError);

    return () => {
      peer.off('stream', handleStream);
      peer.off('close', handleClose);
      peer.off('error', handleError);
    };
  }, [peer, name]);

  return (
    <div style={styles.videoTile}>
      <video
        ref={ref}
        playsInline
        autoPlay
        style={{ ...styles.videoElement, display: hasStream ? 'block' : 'none' }}
      />
      {!hasStream && (
        <div style={styles.placeholder}>
          <Avatar sx={{ width: 60, height: 60, mb: 1 }}>
            <Person />
          </Avatar>
          <Typography variant="body2" sx={{ color: '#aaa' }}>
            Connecting to {name}...
          </Typography>
        </div>
      )}
      <div style={styles.nameOverlay}>
        <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600 }}>
          {name}
        </Typography>
      </div>
    </div>
  );
});

RemoteVideo.displayName = 'RemoteVideo';

// ─────────────────────────────────────────────
// Main VideoChatPage
// ─────────────────────────────────────────────
const VideoChatPage = () => {
  const { roomId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const localVideoRef   = useRef();
  const socketRef       = useRef();
  const peersRef        = useRef([]);   // [{ peerId, peer, name, originalName, userId }]
  const streamRef       = useRef();
  const hasJoinedRef    = useRef(false);

  // ── Anonymous mode refs ──────────────────────────────
  const anonymousRef      = useRef(false);
  const blurRef           = useRef(null); // { track, stream, stop }
  const audioContextRef   = useRef(null);
  const voiceChainRef     = useRef(null); // { input, destination, stop }
  const disguisedAudioRef = useRef(null); // MediaStreamTrack

  const [peers, setPeers]                       = useState([]);
  const [muted, setMuted]                       = useState(false);
  const [videoOff, setVideoOff]                 = useState(false);
  const [chat, setChat]                         = useState([]);
  const [msg, setMsg]                           = useState('');
  const [connectionStatus, setConnectionStatus] = useState('initializing');
  const [showChat, setShowChat]                 = useState(true);
  const [mediaReady, setMediaReady]             = useState(false);
  const [mediaError, setMediaError]             = useState(null);
  const [anonymous, setAnonymous]               = useState(false);

  // ─── Apply/remove anonymous tracks on a single peer connection ─────────
  const applyAnonymousToPeer = useCallback((peer) => {
    if (!peer?._pc?.getSenders) return;
    const senders = peer._pc.getSenders();

    if (anonymousRef.current) {
      const videoTrack = blurRef.current?.track;
      const audioTrack = disguisedAudioRef.current;
      senders.forEach(s => {
        if (s.track?.kind === 'video' && videoTrack) s.replaceTrack(videoTrack).catch(() => {});
        if (s.track?.kind === 'audio' && audioTrack) s.replaceTrack(audioTrack).catch(() => {});
      });
    } else {
      const originalVideo = streamRef.current?.getVideoTracks()[0];
      const originalAudio = streamRef.current?.getAudioTracks()[0];
      senders.forEach(s => {
        if (s.track?.kind === 'video' && originalVideo) s.replaceTrack(originalVideo).catch(() => {});
        if (s.track?.kind === 'audio' && originalAudio) s.replaceTrack(originalAudio).catch(() => {});
      });
    }
  }, []);

  // ─── Toggle anonymous mode ──────────────────────────────
  const toggleAnonymous = useCallback(() => {
    const next = !anonymousRef.current;

    if (next) {
      if (localVideoRef.current) {
        blurRef.current = createBlurredVideoTrack(localVideoRef.current);
      }

      if (streamRef.current) {
        if (!audioContextRef.current) {
          audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }
        const audioCtx = audioContextRef.current;
        const micSource = audioCtx.createMediaStreamSource(streamRef.current);
        const chain = createVoiceDisguiseChain(audioCtx);
        micSource.connect(chain.input);
        voiceChainRef.current = chain;
        disguisedAudioRef.current = chain.destination.stream.getAudioTracks()[0];
      }

      anonymousRef.current = true;
      setAnonymous(true);

      peersRef.current.forEach(({ peer }) => applyAnonymousToPeer(peer));

      socketRef.current?.emit('anonymous-toggle', { roomId, userId: user?.id, anonymous: true });
      toast.info('Anonymous mode on — your name, video, and voice are disguised for others.');
    } else {
      anonymousRef.current = false;
      setAnonymous(false);

      peersRef.current.forEach(({ peer }) => applyAnonymousToPeer(peer));

      blurRef.current?.stop();
      blurRef.current = null;
      voiceChainRef.current?.stop();
      voiceChainRef.current = null;
      disguisedAudioRef.current = null;

      socketRef.current?.emit('anonymous-toggle', { roomId, userId: user?.id, anonymous: false });
      toast.info('Anonymous mode off.');
    }
  }, [roomId, user, applyAnonymousToPeer]);

  // ─── Media acquisition ─────────────────────────────────
  const getUserMedia = useCallback(async () => {
    try {
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: { echoCancellation: true, noiseSuppression: true }
        });
      } catch {
        stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      }

      streamRef.current = stream;
      if (localVideoRef.current) localVideoRef.current.srcObject = stream;
      setMediaReady(true);
      setMediaError(null);
      return stream;
    } catch (err) {
      console.error('[VideoChat] Media error:', err.message);
      setMediaError(err.message);

      const msgs = {
        NotAllowedError  : 'Camera/mic permission denied. Please allow access.',
        NotFoundError    : 'No camera/mic found on this device.',
        NotReadableError : 'Camera/mic in use by another app.',
      };
      toast.error(msgs[err.name] || 'Cannot access camera/microphone.');
      return null;
    }
  }, []);

  // ─── Leave call ────────────────────────────────────────
  const leaveCall = useCallback(() => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('leave-video-room', { roomId, userId: user?.id });
      socketRef.current.emit('end-call', { roomId });
    }

    peersRef.current.forEach(p => { try { p.peer?.destroy(); } catch {} });
    peersRef.current = [];
    setPeers([]);

    if (socketRef.current) {
      try { socketRef.current.off(); socketRef.current.disconnect(); } catch {}
      socketRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => { try { t.stop(); } catch {} });
      streamRef.current = null;
    }

    blurRef.current?.stop();
    blurRef.current = null;
    voiceChainRef.current?.stop();
    voiceChainRef.current = null;
    disguisedAudioRef.current = null;
    if (audioContextRef.current) {
      try { audioContextRef.current.close(); } catch {}
      audioContextRef.current = null;
    }
    anonymousRef.current = false;

    hasJoinedRef.current = false;

    navigate(user?.role === 'doctor' ? '/dochome' : '/home', { replace: true });
  }, [roomId, user, navigate]);

  // ─── Create peer (we initiate) ─────────────────────────
  const createPeer = useCallback((targetSocketId, mySocketId, stream) => {
    const peer = new Peer({
      initiator: true,
      trickle: true,
      stream: stream || undefined,
      config: { iceServers: ICE_SERVERS }
    });

    peer.on('signal', signal => {
      socketRef.current?.connected &&
        socketRef.current.emit('offer', { signal, targetSocketId, roomId });
    });

    peer.on('error', err => console.error('[Peer] createPeer error:', err.message));

    peer.on('connect', () => {
      if (anonymousRef.current) applyAnonymousToPeer(peer);
    });

    return peer;
  }, [roomId, applyAnonymousToPeer]);

  // ─── Add peer (they initiate) ──────────────────────────
  const addPeer = useCallback((incomingSignal, callerSocketId, stream) => {
    const peer = new Peer({
      initiator: false,
      trickle: true,
      stream: stream || undefined,
      config: { iceServers: ICE_SERVERS }
    });

    peer.on('signal', signal => {
      socketRef.current?.connected &&
        socketRef.current.emit('answer', { signal, targetSocketId: callerSocketId, roomId });
    });

    peer.on('error', err => console.error('[Peer] addPeer error:', err.message));

    peer.on('connect', () => {
      if (anonymousRef.current) applyAnonymousToPeer(peer);
    });

    try { peer.signal(incomingSignal); } catch (e) {
      console.error('[Peer] signal error:', e.message);
    }

    return peer;
  }, [roomId, applyAnonymousToPeer]);

  // ─── Socket init ───────────────────────────────────────
  const initializeSocket = useCallback(async (stream) => {
    const token = localStorage.getItem('token');
    if (!token) { toast.error('No auth token'); navigate('/login'); return; }

    setConnectionStatus('connecting');

    // Was hardcoded to a LAN IP (192.168.29.87) via REACT_APP_SOCKET_URL,
    // which is not set anywhere — so it only ever worked on one machine.
    const SOCKET_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3000';

    const socket = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      auth: { token },
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1500,
      reconnectionDelayMax: 5000
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      setConnectionStatus('connected');

      if (!hasJoinedRef.current) {
        hasJoinedRef.current = true;
        socket.emit('join-video-room', { roomId });
      }
    });

    socket.on('connect_error', err => {
      console.error('[VideoChat] Connect error:', err.message);
      setConnectionStatus('error');
      toast.error('Failed to connect to video service');
    });

    socket.on('disconnect', reason => {
      console.log(`[VideoChat] Disconnected: ${reason}`);
      setConnectionStatus('disconnected');
    });

    socket.on('reconnect_attempt', () => {
      setConnectionStatus('reconnecting');
    });

    socket.on('reconnect', () => {
      setConnectionStatus('connected');
      toast.success('Reconnected');

      hasJoinedRef.current = true;
      socket.emit('join-video-room', { roomId });
    });

    socket.on('reconnect_failed', () => {
      toast.error('Lost connection to the call. Please rejoin.');
      leaveCall();
    });

    // ── room-users: sent to the NEW joiner about existing users ──
    socket.on('room-users', (users) => {
      peersRef.current.forEach(p => { try { p.peer?.destroy(); } catch {} });
      peersRef.current = [];

      const freshPeers = [];
      const seenSocketIds = new Set();

      users.forEach(u => {
        if (u.userId === user.id) return;
        if (u.socketId === socket.id) return;
        if (seenSocketIds.has(u.socketId)) return;

        seenSocketIds.add(u.socketId);

        const peer = createPeer(u.socketId, socket.id, stream);
        const entry = {
          peerId: u.socketId,
          peer,
          name: u.anonymous ? 'Anonymous' : u.userName,
          originalName: u.userName,
          userId: u.userId
        };
        peersRef.current.push(entry);
        freshPeers.push({ peer, name: entry.name, userId: u.userId });
      });

      setPeers(freshPeers);
    });

    // ── user-joined-room: sent to EXISTING users when someone new joins ──
    socket.on('user-joined-room', (payload) => {
      if (payload.userId === user.id) return;
      if (peersRef.current.some(p => p.peerId === payload.socketId)) return;

      const peer = createPeer(payload.socketId, socket.id, stream);
      const entry = {
        peerId: payload.socketId,
        peer,
        name: payload.userName,
        originalName: payload.userName,
        userId: payload.userId
      };
      peersRef.current.push(entry);
      setPeers(prev => [...prev, { peer, name: entry.name, userId: payload.userId }]);
      toast.info(`${payload.userName} joined`);
    });

    // ── offer ─────────────────────────────────────────────
    // With trickle enabled, simple-peer emits `signal` repeatedly: the SDP
    // offer first, then each ICE candidate as it is discovered. The server
    // relays all of them through this same event.
    //
    // The previous version returned early whenever a peer already existed for
    // the sender, which discarded every candidate after the first. Connections
    // only succeeded when the first candidate happened to be a viable one —
    // the cause of "calls sometimes don't connect".
    socket.on('offer', ({ signal, fromSocketId, userName, fromUserId }) => {
      if (fromUserId === user.id) return;

      const existing = peersRef.current.find(p => p.peerId === fromSocketId);
      if (existing?.peer) {
        try { existing.peer.signal(signal); }
        catch (e) { console.error('[VideoChat] Trickle signal error:', e.message); }
        return;
      }

      console.log(`[VideoChat] Offer from: ${userName}`);
      const peer = addPeer(signal, fromSocketId, stream);
      const entry = {
        peerId: fromSocketId,
        peer,
        name: userName,
        originalName: userName,
        userId: fromUserId
      };
      peersRef.current.push(entry);
      setPeers(prev => [...prev, { peer, name: entry.name, userId: fromUserId }]);
    });

    // ── answer ────────────────────────────────────────────
    socket.on('answer', ({ signal, fromSocketId }) => {
      const item = peersRef.current.find(p => p.peerId === fromSocketId);
      if (item?.peer) {
        try { item.peer.signal(signal); }
        catch (e) { console.error('[VideoChat] Answer signal error:', e.message); }
      }
    });

    // ── user left ─────────────────────────────────────────
    socket.on('user-left-room', ({ userId: leftId, userName, socketId: leftSocketId }) => {
      const idx = peersRef.current.findIndex(
        p => p.peerId === leftSocketId || p.userId === leftId
      );
      if (idx !== -1) {
        try { peersRef.current[idx].peer?.destroy(); } catch {}
        peersRef.current.splice(idx, 1);
        setPeers(prev => prev.filter((_, i) => i !== idx));
        toast.info(`${userName} left`);
      }
    });

    // ── chat messages ─────────────────────────────────────
    socket.on('receive-message', incoming => setChat(prev => [...prev, incoming]));

    // ── peer toggled anonymous mode ───────────────────────
    socket.on('peer-anonymous-toggle', ({ userId: remoteUserId, anonymous: remoteAnon }) => {
      peersRef.current = peersRef.current.map(p =>
        p.userId === remoteUserId
          ? { ...p, name: remoteAnon ? 'Anonymous' : p.originalName }
          : p
      );
      setPeers(prev => prev.map(p => {
        const match = peersRef.current.find(pr => pr.peer === p.peer);
        return match ? { ...p, name: match.name } : p;
      }));
    });

    // ── call ended by other party ─────────────────────────
    socket.on('call-ended', (data) => {
      toast.info(`Call ended${data?.endedBy ? ` by ${data.endedBy}` : ''}`);
      leaveCall();
    });

  }, [user, roomId, createPeer, addPeer, leaveCall, navigate]);

  // ─── Mount / Unmount ───────────────────────────────────
  useEffect(() => {
    if (!user) { toast.error('Authentication required'); navigate('/login'); return; }

    let mounted = true;

    const init = async () => {
      const stream = await getUserMedia();
      if (!mounted) return;
      await initializeSocket(stream);
    };

    const timer = setTimeout(init, 300);

    return () => {
      mounted = false;
      clearTimeout(timer);

      peersRef.current.forEach(p => { try { p.peer?.destroy(); } catch {} });
      if (socketRef.current) {
        try {
          socketRef.current.emit('leave-video-room', { roomId });
          socketRef.current.off();
          socketRef.current.disconnect();
        } catch {}
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => { try { t.stop(); } catch {} });
      }
      blurRef.current?.stop();
      voiceChainRef.current?.stop();
      if (audioContextRef.current) {
        try { audioContextRef.current.close(); } catch {}
      }
      hasJoinedRef.current = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Controls ──────────────────────────────────────────
  const toggleMute = useCallback(() => {
    if (!streamRef.current) return;
    const next = !muted;
    streamRef.current.getAudioTracks().forEach(t => { t.enabled = !next; });
    setMuted(next);
  }, [muted]);

  const toggleVideo = useCallback(() => {
    if (!streamRef.current) return;
    const next = !videoOff;
    streamRef.current.getVideoTracks().forEach(t => { t.enabled = !next; });
    setVideoOff(next);
  }, [videoOff]);

  // ─── Chat masking (server re-applies this, so a modified client can't bypass it)
  const maskSensitiveData = (text) => {
    if (!text) return text;

    let clean = text.replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/gi, '[HIDDEN EMAIL]');
    clean = clean.replace(/(?:\d[\s-]*){8,14}\d/g, '[HIDDEN NUMBER]');
    clean = clean.replace(/(?:https?:\/\/)?(?:www\.)?(?:instagram\.com|ig\.me)\/[a-zA-Z0-9_.]+/gi, '[HIDDEN LINK]');
    clean = clean.replace(/@[a-zA-Z0-9_.]+/gi, '[HIDDEN HANDLE]');

    return clean;
  };

  const sendMessage = useCallback((e) => {
    e.preventDefault();
    if (!msg.trim() || !socketRef.current?.connected) return;

    const message = {
      id: Date.now(),
      sender: anonymous ? 'Anonymous' : user.name,
      senderId: user.id,
      text: maskSensitiveData(msg.trim()),
      timestamp: new Date()
    };

    socketRef.current.emit('send-message', { roomId, message });
    setChat(prev => [...prev, message]);
    setMsg('');
  }, [msg, user, roomId, anonymous]);

  // ─── Status helpers ────────────────────────────────────
  const statusColor = {
    connected    : '#4caf50',
    connecting   : '#ff9800',
    reconnecting : '#ff9800',
    initializing : '#2196f3',
    error        : '#f44336',
    disconnected : '#f44336'
  }[connectionStatus] || '#9e9e9e';

  const statusLabel = {
    initializing : 'INITIALIZING',
    connecting   : 'CONNECTING',
    reconnecting : 'RECONNECTING…',
    connected    : 'CONNECTED',
    error        : 'ERROR',
    disconnected : 'DISCONNECTED'
  }[connectionStatus] || 'UNKNOWN';

  // ─── Render ────────────────────────────────────────────
  return (
    <div id="video-chat-root">
      <Paper style={styles.statusBar} elevation={3}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box style={{ ...styles.statusDot, background: statusColor }} />
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
            {statusLabel} • Room: {roomId}
          </Typography>
        </Box>
        <Typography variant="body2">
          {peers.length + 1} participant{peers.length + 1 !== 1 ? 's' : ''}
        </Typography>
      </Paper>

      <Box style={styles.videoGrid}>
        <div style={styles.videoTile}>
          <video
            ref={localVideoRef}
            muted autoPlay playsInline
            style={{
              ...styles.videoElement,
              transform: 'scaleX(-1)',
              display: (videoOff || !mediaReady) ? 'none' : 'block'
            }}
          />
          {(videoOff || !mediaReady) && (
            <div style={styles.placeholder}>
              <Avatar sx={{ width: 60, height: 60, mb: 1 }}><Person /></Avatar>
              <Typography variant="body2" sx={{ color: '#aaa' }}>
                {mediaError ? 'Camera Error' : videoOff ? 'Video off' : 'Starting...'}
              </Typography>
              {mediaError && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, textAlign: 'center' }}>
                  {mediaError}
                </Typography>
              )}
            </div>
          )}
          <div style={styles.nameOverlay}>
            <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600 }}>
              {anonymous ? 'Anonymous (You)' : `You (${user?.name})`}
            </Typography>
          </div>
        </div>

        {peers.map(({ peer, name, userId }, i) => (
          <RemoteVideo key={`${userId}-${i}`} peer={peer} name={name} />
        ))}

        {peers.length === 0 && connectionStatus === 'connected' && (
          <Box style={styles.waiting}>
            <Avatar sx={{ width: 80, height: 80, mb: 2, bgcolor: 'primary.main' }}>
              <Person sx={{ fontSize: 40 }} />
            </Avatar>
            <Typography variant="h6" sx={{ color: '#fff' }}>
              Waiting for participants...
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
              Room: {roomId}
            </Typography>
          </Box>
        )}

        {connectionStatus === 'initializing' && (
          <Box style={styles.waiting}>
            <Typography variant="h6" sx={{ color: '#fff' }}>
              Setting up video call...
            </Typography>
          </Box>
        )}
      </Box>

      <Paper style={styles.controls} elevation={6}>
        <IconButton
          onClick={toggleMute}
          disabled={!mediaReady}
          sx={muted ? styles.activeBtn : styles.controlBtn}
        >
          {muted ? <MicOff /> : <Mic />}
        </IconButton>

        <IconButton
          onClick={toggleVideo}
          disabled={!mediaReady}
          sx={videoOff ? styles.activeBtn : styles.controlBtn}
        >
          {videoOff ? <VideocamOff /> : <Videocam />}
        </IconButton>

        <IconButton
          onClick={toggleAnonymous}
          disabled={!mediaReady}
          sx={anonymous ? styles.activeBtn : styles.controlBtn}
          title={anonymous ? 'Turn off anonymous mode' : 'Go anonymous'}
        >
          {anonymous ? <VisibilityOff /> : <Visibility />}
        </IconButton>

        <IconButton onClick={leaveCall} sx={styles.hangupBtn}>
          <CallEnd />
        </IconButton>

        <IconButton
          onClick={() => setShowChat(s => !s)}
          sx={showChat ? styles.activeBtn : styles.controlBtn}
        >
          <Badge badgeContent={chat.length} color="primary" max={99}>
            <ChatIcon />
          </Badge>
        </IconButton>
      </Paper>

      {showChat && (
        <Paper style={styles.chatPanel} elevation={4}>
          <Typography variant="h6" sx={{ p: 1.5, borderBottom: '1px solid #e0e0e0' }}>
            Chat
          </Typography>
          <Box style={styles.chatMessages}>
            {chat.map((m, i) => (
              <Box
                key={m.id || i}
                sx={{ p: 1, background: '#f5f5f5', borderRadius: 2, mb: 0.5 }}
              >
                <Typography variant="caption" color="textSecondary">{m.sender}</Typography>
                <Typography variant="body2">{m.text}</Typography>
              </Box>
            ))}
          </Box>
          <Box component="form" onSubmit={sendMessage} style={styles.chatInput}>
            <TextField
              value={msg}
              onChange={e => setMsg(e.target.value)}
              placeholder="Type a message..."
              variant="outlined" size="small" fullWidth
            />
            <IconButton type="submit" color="primary"><Send /></IconButton>
          </Box>
        </Paper>
      )}

      <style>{`
        #video-chat-root {
          height: 100vh;
          display: flex;
          flex-direction: column;
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
          position: relative;
          font-family: inherit;
        }
        @keyframes vcPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.5; transform: scale(1.2); }
        }
      `}</style>
    </div>
  );
};

// ─────────────────────────────────────────────
// Inline styles
// ─────────────────────────────────────────────
const styles = {
  statusBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.75rem 1.25rem',
    background: '#fff',
    zIndex: 10,
    flexShrink: 0,
  },
  statusDot: {
    width: 12,
    height: 12,
    borderRadius: '50%',
    animation: 'vcPulse 2s infinite',
    flexShrink: 0,
  },
  videoGrid: {
    flex: 1,
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '0.75rem',
    padding: '0.75rem',
    overflowY: 'auto',
    alignContent: 'start',
  },
  videoTile: {
    position: 'relative',
    background: '#1a1a1a',
    borderRadius: '10px',
    overflow: 'hidden',
    aspectRatio: '16/9',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  videoElement: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  placeholder: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    textAlign: 'center',
    padding: '1.5rem',
  },
  nameOverlay: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    background: 'linear-gradient(transparent, rgba(0,0,0,0.75))',
    padding: '0.75rem',
  },
  waiting: {
    gridColumn: '1 / -1',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3rem',
    textAlign: 'center',
  },
  controls: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '1.25rem',
    background: '#fff',
    flexShrink: 0,
  },
  controlBtn: {
    background: '#f5f5f5',
    width: 52,
    height: 52,
  },
  activeBtn: {
    background: '#f44336 !important',
    color: '#fff !important',
    width: 52,
    height: 52,
  },
  hangupBtn: {
    background: '#f44336 !important',
    color: '#fff !important',
    width: 60,
    height: 60,
  },
  chatPanel: {
    position: 'fixed',
    right: '1rem',
    bottom: '6rem',
    width: 300,
    maxHeight: 400,
    display: 'flex',
    flexDirection: 'column',
    zIndex: 20,
    borderRadius: 12,
  },
  chatMessages: {
    flex: 1,
    overflowY: 'auto',
    padding: '0.75rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.4rem',
  },
  chatInput: {
    display: 'flex',
    gap: '0.5rem',
    padding: '0.75rem',
    borderTop: '1px solid #e0e0e0',
  },
};

export default VideoChatPage;